import type { BankExpenseCategorizationRule, MonthlyReportExpenseLine } from '../types';

/** Stable key for matching recurring bank descriptions (e.g. "Chevalier Produce · Chevalier"). */
export function normalizeBankTransactionPatternKey(label: string): string {
  return label
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

export function applyCategorizationRuleToLine(
  base: MonthlyReportExpenseLine,
  rule: BankExpenseCategorizationRule
): MonthlyReportExpenseLine {
  const t = rule.expenseType;
  if (t === 'wage') {
    return {
      ...base,
      isWageOrPayrollExpense: true,
      isWageGstClaimable: false,
      gstClaimable: false,
      zeroGst: true,
      expenseCategory: undefined,
      autoCategorized: true,
    };
  }
  if (t === 'wage_gst') {
    return {
      ...base,
      isWageOrPayrollExpense: true,
      isWageGstClaimable: true,
      gstClaimable: true,
      zeroGst: false,
      expenseCategory: undefined,
      autoCategorized: true,
    };
  }
  const expCat = rule.expenseCategory || '';
  if (rule.gstMode === 'claimable') {
    return {
      ...base,
      isWageOrPayrollExpense: false,
      isWageGstClaimable: false,
      gstClaimable: true,
      zeroGst: false,
      expenseCategory: expCat,
      autoCategorized: true,
    };
  }
  if (rule.gstMode === 'zero') {
    return {
      ...base,
      isWageOrPayrollExpense: false,
      isWageGstClaimable: false,
      gstClaimable: false,
      zeroGst: true,
      expenseCategory: expCat,
      autoCategorized: true,
    };
  }
  // unset — user must choose GST; still apply category
  return {
    ...base,
    isWageOrPayrollExpense: false,
    isWageGstClaimable: false,
    gstClaimable: false,
    zeroGst: false,
    expenseCategory: expCat,
    autoCategorized: true,
  };
}

export function ruleFromSavedExpenseLine(
  line: MonthlyReportExpenseLine
): Omit<BankExpenseCategorizationRule, 'id' | 'patternKey' | 'updatedAt'> & { patternKey: string } {
  const label = `${line.label ?? ''}`.trim();
  const patternKey = normalizeBankTransactionPatternKey(label);
  const isWage = line.isWageOrPayrollExpense === true;
  const wageGst = isWage && line.isWageGstClaimable === true;
  if (wageGst) {
    return {
      patternKey,
      sourceLabel: label,
      expenseCategory: '',
      expenseType: 'wage_gst',
      gstMode: 'claimable',
    };
  }
  if (isWage) {
    return {
      patternKey,
      sourceLabel: label,
      expenseCategory: '',
      expenseType: 'wage',
      gstMode: 'zero',
    };
  }
  const mode: 'claimable' | 'zero' | 'unset' =
    line.gstClaimable && !line.zeroGst ? 'claimable' : line.zeroGst && !line.gstClaimable ? 'zero' : 'unset';
  return {
    patternKey,
    sourceLabel: label,
    expenseCategory: `${line.expenseCategory ?? ''}`.trim(),
    expenseType: 'operating',
    gstMode: mode,
  };
}
